"use client"

import { useState, useCallback, useRef } from "react"
import type { TEnrichEmailResponse, TEnrichedPersonData } from "@/app/api/enrich/email/schema"

interface UseEmailEnrichmentOptions {
  onSuccess?: (data: TEnrichedPersonData) => void
  onError?: (error: string) => void
  onRateLimited?: () => void
  onAlreadyEnriched?: () => void
}

interface UseEmailEnrichmentReturn {
  enrich: (email: string) => Promise<void>
  isLoading: boolean
  data: TEnrichedPersonData | null
  error: string | null
  isRateLimited: boolean
  wasEnriched: boolean
}

/**
 * Hook for email enrichment via Apollo.io
 * Handles rate limiting, caching, and error states
 */
export function useEmailEnrichment(options: UseEmailEnrichmentOptions = {}): UseEmailEnrichmentReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<TEnrichedPersonData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isRateLimited, setIsRateLimited] = useState(false)
  const [wasEnriched, setWasEnriched] = useState(false)

  // Track enriched emails in session to prevent duplicate calls
  const enrichedEmailsRef = useRef<Set<string>>(new Set())

  const enrich = useCallback(
    async (email: string) => {
      const normalizedEmail = email.toLowerCase().trim()

      // Skip if already enriched in this session (client-side check)
      if (enrichedEmailsRef.current.has(normalizedEmail)) {
        options.onAlreadyEnriched?.()
        return
      }

      // Basic email validation
      if (!normalizedEmail || !normalizedEmail.includes("@")) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        console.log("[useEmailEnrichment] Fetching enrichment for:", normalizedEmail)

        const response = await fetch("/api/enrich/email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail }),
        })

        console.log("[useEmailEnrichment] Response status:", response.status)

        const result = (await response.json()) as TEnrichEmailResponse

        console.log("[useEmailEnrichment] Result:", JSON.stringify(result, null, 2))

        if (result.rateLimited) {
          console.log("[useEmailEnrichment] Rate limited")
          setIsRateLimited(true)
          options.onRateLimited?.()
          return
        }

        if (result.alreadyEnriched) {
          console.log("[useEmailEnrichment] Already enriched")
          enrichedEmailsRef.current.add(normalizedEmail)
          options.onAlreadyEnriched?.()
          return
        }

        if (result.success && result.data) {
          console.log("[useEmailEnrichment] Success! Data:", result.data)
          enrichedEmailsRef.current.add(normalizedEmail)
          setData(result.data)
          setWasEnriched(true)
          options.onSuccess?.(result.data)
        } else if (result.error) {
          console.log("[useEmailEnrichment] Error:", result.error)
          // Silent failures for Apollo errors - don't show to user
          setError(result.error)
          options.onError?.(result.error)
        } else {
          console.log("[useEmailEnrichment] No data returned, success:", result.success)
        }
      } catch (err) {
        console.error("[useEmailEnrichment] Fetch error:", err)
        const errorMessage = "Failed to enrich email"
        setError(errorMessage)
        options.onError?.(errorMessage)
      } finally {
        setIsLoading(false)
      }
    },
    [options]
  )

  return {
    enrich,
    isLoading,
    data,
    error,
    isRateLimited,
    wasEnriched,
  }
}
